import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Controller, useFormContext } from 'react-hook-form'

import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { type ExportOptionsValues, IntegrationServiceId, type VisibleToExportOptionConfig } from '../../../../common/integrations'
import { useGetIntegrationEntitiesQuery } from '../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'

export interface UserGroupDropdownProps {
  serviceId: IntegrationServiceId
  config: VisibleToExportOptionConfig
}

export const UserGroupDropdown: React.FC<UserGroupDropdownProps> = ({ serviceId, config }) => {
  const accountId = useAppSelector(selectAccountId)
  const { control } = useFormContext<{ exportOptions?: ExportOptionsValues }>()

  const { data, isFetching } = useGetIntegrationEntitiesQuery({ accountId, connector: serviceId, entityType: config.entityType }, { skip: !accountId })

  const userGroupOptions = useMemo<SelectOption[]>(() => {
    const userGroups = data?.data.userGroups ?? []
    const sharedGroups = userGroups.filter((userGroup) => userGroup.user === null)
    const personalGroups = userGroups.filter((userGroup) => userGroup.user !== null)
    return [...sharedGroups, ...personalGroups].map((userGroup) => ({ id: userGroup.visibleTo, label: userGroup.name }))
  }, [data])

  return (
    <Row className='mt-3'>
      <Col xs={12}>
        <div className='lead-extra-small'>Visible to</div>
        <Controller
          name='exportOptions.visibleTo'
          control={control}
          render={({ field }) => (
            <SelectDropdown
              toggleId='visibleToDropdown'
              ariaLabel='Visible to'
              initialSelectedOption={userGroupOptions.find((option) => option.id === field.value) ?? null}
              options={userGroupOptions}
              className='py-2 zocks-px-0.75'
              onOptionSelected={(option) => field.onChange(option.id)}
              onDeselect={() => field.onChange(null)}
              searchable={true}
              loading={isFetching}
              placeholder='Default visibility'
              deselectOptionLabel='Default visibility'
              showDeselectOption={true}
            />
          )}
        />
      </Col>
    </Row>
  )
}
